import { LOADING_CONTENT } from '@/core/store/types/action-types';
import { Vue, Component, Watch } from 'vue-property-decorator';
import { FullscreenControl, LngLat, LngLatBounds, Map, NavigationControl, Popup } from 'maplibre-gl';
import tasksClient from '@/core/api/tasks-service';
import sitesClient from '@/core/api/sites-service';
import realEstateClient from '@/core/api/real-estate-service';
import { RealEstateRoomDTO, AutocompleteDTO } from '@/../client-generator/generated-clients/real-estate';

interface MapRoomProprieties {
  CODICE_EDI: string;
  CODICE_LOC: string;
  CODICE_PIA: string;
  CODICE_UBI: string;
  DATA_VALID: string;
  FREQUENZA: number;
  /* eslint no-underscore-dangle: 0 */
  ID_LOCALE_: string;
  NOME_EDIFI: string;
  NOME_LOCAL: string;
  NOME_PIANO: string;
  PERIODICI: string;
  STATO: string;
  SUPERFICIE: number;
  TARIFFA: number;
  TIPO_AREA: number;
  TURNI: number;
  VETRI: string;
}

@Component
export default class RealEstateRegistry extends Vue {
  selectedPlace = '';
  selectedJobTask = '';
  showLightbox = false;
  selectedFloor: any = '';
  map: Map | null = null;
  hoveredStateId: any = null;

  places: AutocompleteDTO[] = [];
  floors: AutocompleteDTO[] = [];
  jobTasks: AutocompleteDTO[] = [];

  roomDetail: RealEstateRoomDTO | null = null;

  lightBoxExpanded = false;

  @Watch('selectedJobTask')
  selectedJobTaskUpdated(newValue) {
    if (newValue) {
      this.fetchPlaces();
      this.doCleanup(true);
    }
  }

  @Watch('selectedPlace')
  selectedPlaceUpdated(newValue) {
    if (newValue) {
      this.fetchFloors();
      this.doCleanup();
    }
  }

  @Watch('selectedFloor')
  async selectedFloorUpdated(newValue, precValue) {
    if (newValue != null) {
      this.loadMapFM(this.selectedFloor);
      // this.loadMapFM('TEATRO', 'PT01');
    } else {
      this.roomDetail = null;
      this.showLightbox = false;
    }

    if (precValue == null) {
      this.$nextTick(() => {
        this.map = new Map({
          container: 'map',

          style: {
            version: 8,
            sources: {},
            layers: [
              {
                id: 'background',
                type: 'background',
                paint: { 'background-color': 'white' },
              },
            ],
          },
          hash: !1,
          pitch: 0,
          bearing: 0,
        });

        this.map.addControl(new NavigationControl({}));
        this.map.addControl(new FullscreenControl({}));
        this.map.on('click', 'rooms', (event) => {
          if (event.features) this.fetchRoomDetail(event.features[0].properties as any);
        });

        this.map.on('load', () => this.loadMapFM(this.selectedFloor));
        // this.map.on('load', () => this.loadMapFM('TEATRO', 'PT01'));
      });
    }

    this.showLightbox = false;
  }

  get isPlaceSelected() {
    return this.selectedPlace && this.selectedFloor;
  }

  get showJobTaskSelect() {
    return this.jobTasks.length > 1;
  }

  mounted() {
    this.fetchJobTasks();
  }

  metadataCallback(data) {
    if (!this.map) return;
    const coordinates = data.wgs84Extent.coordinates;
    const sw = new LngLat(coordinates[0][0][0], coordinates[0][1][1]);
    const ne = new LngLat(coordinates[0][2][0], coordinates[0][0][1]);
    const bounds = new LngLatBounds(sw, ne);

    this.map.fitBounds(bounds, {
      linear: true,
      padding: 20,
    });
  }

  fetchJSONFile(path, callback) {
    const httpRequest = new XMLHttpRequest();
    httpRequest.onreadystatechange = () => {
      if (httpRequest.readyState === 4 && httpRequest.status === 200) {
        const data = JSON.parse(httpRequest.responseText);
        if (callback) callback(data);
      }
    };
    httpRequest.open('GET', path);
    httpRequest.send();
  }

  loadMapFM(floor) {
    if (!this.map) return;

    const buildingId = floor.split('-')[0];
    const floorId = floor.split('-')[1];

    const sourceGeoJson = `https://portale.pulinet.it/fm/data/${buildingId}/${floorId}/ATTRIBUTI.json`;
    const sourceRasterPlan = `https://portale.pulinet.it/fm/data/${buildingId}/${floorId}/{z}/{x}/{y}.png`;

    const metadata = `https://portale.pulinet.it/fm/data/${buildingId}/${floorId}/metadata.json`;

    // CORS problem using axios, use XMLHttpRequest instead
    this.fetchJSONFile(metadata, this.metadataCallback);

    if (this.map.getLayer('rooms') != null) {
      this.map.removeLayer('rooms');
    }

    if (this.map.getSource('rooms') != null) {
      this.map.removeSource('rooms');
    }

    this.map.addSource('rooms', {
      type: 'geojson',
      data: sourceGeoJson,
    });

    this.map.addLayer({
      id: 'rooms',
      type: 'fill-extrusion',
      source: 'rooms',
      layout: {
        visibility: 'visible',
      },
      paint: {
        'fill-extrusion-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          '#FFFF00',
          [
            'match',
            ['get', 'FREQUENZA'],
            0,
            '#69d456',
            1,
            '#3299d1',
            2,
            '#cee32b',
            3,
            '#bc70c9',
            4,
            '#d81563',
            5,
            '#d98452',
            6,
            '#4738c8',
            7,
            '#4738c8',
            8,
            '#4738c8',
            '#ccc',
          ],
        ],
        'fill-extrusion-height': 0.1,
        'fill-extrusion-base': 0,
        'fill-extrusion-opacity': 0.8,
      },
    });

    // Create a popup, but don't add it to the map yet.
    const popup = new Popup({
      closeButton: false,
      closeOnClick: false,
    });

    if (this.map.getLayer('raster-tiles') != null) {
      this.map.removeLayer('raster-tiles');
    }

    if (this.map.getSource('raster-layout') != null) {
      this.map.removeSource('raster-layout');
    }

    // Raster background
    this.map.addSource('raster-layout', {
      type: 'raster',
      tiles: [sourceRasterPlan],
      tileSize: 256,
      attribution: 'Pulinet Servizi',
    });

    this.map.on('mousemove', 'rooms', (e: any) => {
      if (e.features.length > 0) {
        if (this.hoveredStateId !== null && this.map) {
          this.map.setFeatureState(
            {
              source: 'rooms',
              id: this.hoveredStateId,
            },
            {
              hover: false,
            }
          );

          // display tooltip
          // Change the cursor style as a UI indicator.
          this.map.getCanvas().style.cursor = 'pointer';

          const coordinates = e.lngLat;
          const description = e.features[0].properties.NOME_LOCAL;

          popup.setLngLat(coordinates).setHTML(description).addTo(this.map);
        }
        this.hoveredStateId = e.features[0].id;
        if (this.map) {
          this.map.setFeatureState(
            {
              source: 'rooms',
              id: this.hoveredStateId,
            },
            {
              hover: true,
            }
          );
        }
      }
    });
    this.map.on('mouseenter', 'rooms', () => {
      if (this.map) this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', 'rooms', () => {
      if (this.hoveredStateId !== null && this.map) {
        this.map.setFeatureState(
          {
            source: 'rooms',
            id: this.hoveredStateId,
          },
          {
            hover: false,
          }
        );
      }
      this.hoveredStateId = null;
      if (this.map) this.map.getCanvas().style.cursor = 'default';
      popup.remove();
    });

    this.map.addLayer({
      id: 'raster-tiles',
      type: 'raster',
      source: 'raster-layout',
      minzoom: 15,
      maxzoom: 24,
    });

    if (this.map != null) this.map.resize();
  }

  async fetchJobTasks() {
    this.$store.dispatch(LOADING_CONTENT, true);
    try {
      this.jobTasks = await tasksClient.autocomplete(null, '', true);
      this.handleJobTasks();
    } catch (error) {
      this.jobTasks = [];
      let messageError = 'error';
      switch (error.status) {
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }
      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  private handleJobTasks() {
    if (this.jobTasks.length === 1) this.selectedJobTask = this.jobTasks[0].id ?? '';
  }

  async fetchPlaces() {
    this.$store.dispatch(LOADING_CONTENT, true);
    try {
      this.places = await sitesClient.customerAutocomplete(this.selectedJobTask, '');
    } catch (error) {
      this.places = [];
      let messageError = 'error';
      switch (error.status) {
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }
      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  async fetchFloors() {
    this.$store.dispatch(LOADING_CONTENT, true);
    try {
      this.floors = await realEstateClient.siteFloors(this.selectedPlace);
    } catch (error) {
      this.floors = [];
      let messageError = 'error';
      switch (error.status) {
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }
      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  async fetchRoomDetail(room: MapRoomProprieties | null) {
    if (!room) return;
    this.$store.dispatch(LOADING_CONTENT, true);

    try {
      this.roomDetail = await realEstateClient.roomDetail(this.selectedPlace, this.selectedFloor, room.CODICE_LOC);
      // this.roomDetail = await realEstateClient.roomDetail('UB01', '02');
      // this.roomDetail.roomName = room.LCOALE;
      this.showLightbox = true;
      this.lightBoxExpanded = true;
    } catch (error) {
      let messageError = 'error';
      switch (error.status) {
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }
      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  doCleanup(isJobTask = false) {
    if (isJobTask) this.selectedPlace = '';
    this.selectedFloor = null;
    this.roomDetail = null;
    this.showLightbox = false;
  }

  handleShowAllClick() {
    this.$router.push({
      name: 'page-real-estate-registry-flat-view',
      path: 'real-estate-flat',
      query: {
        jobTask: this.selectedJobTask,
      },
    });
  }
}
